import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import { accounting, shortId, type AccountingItem } from '../api'

type ToggleField = 'sent_to_ap' | 'oracle_received' | 'paid'
type SortCol = 'vendor' | 'amount' | 'received_at'

function fmtDate(s: string | null): string {
  if (!s) return ''
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) } catch { return '' }
}

// Milestone cell. Auto-matched (has an evidence email) → the date links to that
// email. Manually set → click to clear. Empty → click to mark done (today).
// span[role=button] because this project ships without Tailwind preflight.
function DateCell({ at, evidence, onSet }: {
  at: string | null; evidence: number | null; onSet: (v: boolean) => void
}) {
  const key = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn() }
  }
  if (at && evidence) {
    return (
      <Link to={`/artifact/${evidence}`} title="Open the email that supplied this date"
        className="me-record whitespace-nowrap text-emerald-700 hover:underline">
        {fmtDate(at)}
      </Link>
    )
  }
  if (at) {
    return (
      <span role="button" tabIndex={0} title="Click to clear" onClick={() => onSet(false)} onKeyDown={key(() => onSet(false))}
        className="me-record cursor-pointer whitespace-nowrap text-emerald-700 hover:line-through">
        {fmtDate(at)}
      </span>
    )
  }
  return (
    <span role="button" tabIndex={0} title="Click to mark done (today)" onClick={() => onSet(true)} onKeyDown={key(() => onSet(true))}
      className="cursor-pointer select-none text-black/20 hover:text-black/60">＋</span>
  )
}

function hasContent(it: AccountingItem): boolean {
  return !!(it.vendor || it.invoice_number || it.amount_raw || it.their_matter_no)
}

const TH = 'sticky top-0 z-10 bg-[color:var(--me-bg)] px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-black/40 border-b border-black/10'
const TD = 'px-3 py-1.5 border-b border-black/[0.06] align-top'
const dash = <span className="text-black/25">—</span>

function InvoiceRow({ it, setFlag }: {
  it: AccountingItem
  setFlag: (id: number, f: ToggleField, v: boolean) => void
}) {
  const navigate = useNavigate()
  const [docHover, setDocHover] = useState(false)
  const trace = `/artifact/${it.source_attachment || it.source_email}`
  const docTd = TD + ' cursor-pointer' + (docHover ? ' bg-black/[0.05]' : '')
  const docH = {
    onMouseEnter: () => setDocHover(true),
    onMouseLeave: () => setDocHover(false),
    onClick: () => navigate(trace),
    title: 'Open the source invoice (QA trace)',
  }
  return (
    <tr>
      <td className={TD + ' font-medium'}>
        <Link to={`/artifact/${it.source_email}`} className="text-inherit hover:underline">{it.vendor || dash}</Link>
      </td>
      <td className={docTd} {...docH}>{it.invoice_number || dash}</td>
      <td className={docTd + ' whitespace-nowrap'} {...docH}>{it.amount_raw || dash}</td>
      <td className={docTd + ' whitespace-nowrap text-black/60'} {...docH}>{fmtDate(it.received_at) || '—'}</td>
      <td
        className={TD + (it.linked_matter_seq ? ' cursor-pointer hover:bg-black/[0.05]' : '')}
        onClick={it.linked_matter_seq ? () => navigate(`/workspace?matter=${it.linked_matter}`) : undefined}
        title={it.linked_matter_seq ? 'Open the matter' : undefined}
      >
        {it.linked_matter_seq ? (
          <span className="inline-block max-w-[15rem] truncate align-bottom">
            <span className="me-data">M-{String(it.linked_matter_seq).padStart(3, '0')}</span>{' '}
            <span className="text-black/60">{it.linked_matter_name}</span>
          </span>
        ) : dash}
      </td>
      <td className={TD}><DateCell at={it.sent_to_ap_at} evidence={it.sent_to_ap_email} onSet={(v) => setFlag(it.id, 'sent_to_ap', v)} /></td>
      <td className={TD}><DateCell at={it.paid_at} evidence={it.paid_email} onSet={(v) => setFlag(it.id, 'paid', v)} /></td>
      <td className={TD}><DateCell at={it.oracle_received_at} evidence={it.oracle_email} onSet={(v) => setFlag(it.id, 'oracle_received', v)} /></td>
    </tr>
  )
}

export default function AccountingPage() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const [items, setItems] = useState<AccountingItem[] | null>(null)
  const [error, setError] = useState('')
  const [sort, setSort] = useState<{ col: SortCol; dir: 1 | -1 }>({ col: 'received_at', dir: -1 })

  useEffect(() => {
    if (!user?.has_studio_access) return
    accounting.list().then(setItems).catch(() => setError('Failed to load accounting items.'))
  }, [user?.has_studio_access])

  const setFlag = async (id: number, field: ToggleField, value: boolean) => {
    try {
      const updated = await accounting.setFlag(id, field, value)
      setItems((prev) => (prev ? prev.map((it) => (it.id === id ? updated : it)) : prev))
    } catch { setError('Could not save that change.') }
  }

  if (auth.status === 'authenticated' && !user?.has_studio_access) {
    return <Navigate to="/workspace" replace />
  }

  const invoices = (items || []).filter((i) => i.item_kind === 'invoice' && hasContent(i))
  const admin = (items || []).filter((i) => i.item_kind === 'admin')

  const toggleSort = (col: SortCol) =>
    setSort((s) => (s.col === col ? { col, dir: (s.dir === 1 ? -1 : 1) } : { col, dir: 1 }))
  const sortedInv = [...invoices].sort((a, b) => {
    let av: number | string, bv: number | string
    if (sort.col === 'amount') { av = parseFloat(a.amount || '') || 0; bv = parseFloat(b.amount || '') || 0 }
    else if (sort.col === 'received_at') { av = a.received_at || ''; bv = b.received_at || '' }
    else { av = (a.vendor || '').toLowerCase(); bv = (b.vendor || '').toLowerCase() }
    return av < bv ? -sort.dir : av > bv ? sort.dir : 0
  })
  const sortTh = (label: string, col: SortCol) => (
    <th className={TH + ' cursor-pointer select-none'} onClick={() => toggleSort(col)} title="Sort">
      {label}{sort.col === col ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
    </th>
  )
  const emptyBox = (t: string) => (
    <div className="rounded border border-black/10 px-3 py-4 text-sm text-black/40">{t}</div>
  )

  return (
    <div className="me-scroll h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl px-6 py-6 me-record">
        {error && <div className="mb-4 rounded bg-red-100 px-3 py-2 text-sm text-red-700">{error}</div>}
        {items === null && !error && <div className="text-sm text-black/40">Loading…</div>}

        {items !== null && (
          <>
            <section>
              <h2 className="me-summary mb-2 text-lg font-medium">Payables</h2>
              {sortedInv.length === 0 ? emptyBox('No payables yet.') : (
                <table className="w-full border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      {sortTh('Party', 'vendor')}
                      <th className={TH}>Invoice #</th>
                      {sortTh('Amount', 'amount')}
                      {sortTh('Received', 'received_at')}
                      <th className={TH}>Matter</th>
                      <th className={TH + ' w-20'}>AP</th>
                      <th className={TH + ' w-20'}>Paid</th>
                      <th className={TH + ' w-20'}>Oracle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedInv.map((it) => <InvoiceRow key={it.id} it={it} setFlag={setFlag} />)}
                  </tbody>
                </table>
              )}
            </section>

            <section className="mt-8">
              <h2 className="me-summary mb-2 text-lg font-medium">Non-invoice admin</h2>
              <p className="mb-2 text-xs text-black/40">Payment confirmations, ACH allocations, PO housekeeping.</p>
              {admin.length === 0 ? emptyBox('No admin items yet.') : (
                <table className="w-full border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      <th className={TH}>Party</th>
                      <th className={TH}>Subject</th>
                      <th className={TH}>Received</th>
                      <th className={TH}>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admin.map((it) => (
                      <tr key={it.id}>
                        <td className={TD + ' font-medium'}>
                          <Link to={`/artifact/${it.source_email}`} className="text-inherit hover:underline">{it.vendor || dash}</Link>
                        </td>
                        <td className={TD}>{it.source_email_subject || '—'}</td>
                        <td className={TD + ' whitespace-nowrap text-black/60'}>{fmtDate(it.received_at) || '—'}</td>
                        <td className={TD}>
                          <Link to={`/artifact/${it.source_email}`} className="me-data text-xs text-blue-700 hover:underline">
                            {shortId(it.source_email_composite) || `#${it.source_email}`}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  )
}
